import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  listenerProfile,
  favoriteRadios,
  followedRadios,
  listeningHistory,
  notificationsList,
  type FavoriteRadioEntry,
  type FollowedRadioEntry,
  type ListeningHistoryEntry,
  type NotificationEntry,
} from "@/mocks/listenerCenter";

type SectionTab = "favorites" | "history" | "following" | "notifications" | "config";

const MOCK_AVATARS = [
  "https://readdy.ai/api/search-image?query=Friendly%20Hispanic%20young%20man%20portrait%20with%20genuine%20warm%20smile%2C%20casual%20modern%20style%20wearing%20simple%20navy%20t-shirt%2C%20clean%20soft%20studio%20background%20with%20warm%20natural%20lighting%2C%20approachable%20authentic%20headshot%20photography%2C%20editorial%20portrait%20style&width=400&height=400&seq=profile-avatar-1&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Young%20man%20portrait%20with%20confident%20friendly%20expression%2C%20wearing%20casual%20denim%20jacket%2C%20urban%20street%20style%2C%20soft%20natural%20window%20lighting%20diffused%20warm%20tone%2C%20clean%20minimal%20background%2C%20authentic%20editorial%20photography&width=400&height=400&seq=profile-avatar-2&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Young%20adult%20male%20portrait%20with%20gentle%20smile%2C%20wearing%20black%20hoodie%20casual%20look%2C%20modern%20studio%20lighting%20on%20gradient%20warm%20beige%20background%2C%20contemporary%20headshot%20photography%20clean%20style&width=400&height=400&seq=profile-avatar-3&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Hispanic%20young%20adult%20male%20portrait%20with%20approachable%20confident%20expression%2C%20wearing%20olive%20green%20casual%20button%20shirt%2C%20warm%20studio%20lighting%2C%20clean%20cream%20background%2C%20professional%20headshot%20photography%20friendly%20look&width=400&height=400&seq=profile-avatar-4&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Young%20man%20smiling%20natural%20portrait%2C%20casual%20white%20tshirt%20relaxed%20style%2C%20natural%20sunlight%20through%20window%20creating%20soft%20gentle%20shadows%2C%20warm%20ambient%20atmosphere%2C%20relaxed%20authentic%20lifestyle%20photography&width=400&height=400&seq=profile-avatar-5&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Portrait%20of%20young%20Latin%20man%20with%20warm%20kind%20expression%2C%20wearing%20casual%20light%20blue%20polo%20shirt%2C%20clean%20studio%20background%20with%20soft%20gradient%20lighting%2C%20professional%20yet%20friendly%20approachable%20headshot&width=400&height=400&seq=profile-avatar-6&orientation=squarish",
];

const SECTION_TABS: { key: SectionTab; label: string; icon: string }[] = [
  { key: "favorites", label: "Favoritas", icon: "ri-heart-line" },
  { key: "history", label: "Historial", icon: "ri-history-line" },
  { key: "following", label: "Siguiendo", icon: "ri-user-follow-line" },
  { key: "config", label: "Configuración", icon: "ri-settings-3-line" },
  { key: "notifications", label: "Notificaciones", icon: "ri-notification-3-line" },
];

const LANG_OPTIONS = [
  { code: "es", label: "Español", flag: "🇲🇽" },
  { code: "en", label: "English", flag: "🇺🇸" },
];

function stationSlug(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function formatRelativeTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffHrs = Math.floor((now.getTime() - d.getTime()) / 3600000);
  if (diffHrs < 1) return "Ahora";
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "Ayer";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

function formatMinutes(min: number) {
  if (min >= 60) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }
  return `${min} min`;
}

const badgeColors: Record<string, string> = {
  "Oyente Fiel": "bg-amber-100 text-amber-700",
  Madrugador: "bg-sky-100 text-sky-700",
  "Mega Fan": "bg-rose-100 text-rose-700",
  Comunicador: "bg-emerald-100 text-emerald-700",
  "Oyente Legendario": "bg-violet-100 text-violet-700",
};

export default function AppProfilePage() {
  const navigate = useNavigate();

  // Profile state
  const [avatarUrl, setAvatarUrl] = useState(listenerProfile.avatarUrl);
  const [profileData, setProfileData] = useState({
    name: listenerProfile.name,
    username: listenerProfile.username,
    bio: listenerProfile.bio,
    email: listenerProfile.email,
    location: listenerProfile.location,
  });
  const [language, setLanguage] = useState("es");

  // Modals
  const [editOpen, setEditOpen] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  // Section tab
  const [section, setSection] = useState<SectionTab>("favorites");

  // Editable data
  const [favRadios, setFavRadios] = useState(favoriteRadios);
  const [follows, setFollows] = useState(followedRadios);
  const [notifs, setNotifs] = useState(notificationsList);

  // Edit form state
  const [editForm, setEditForm] = useState({ ...profileData });

  // Notification preferences (for config section)
  const [notifPrefs, setNotifPrefs] = useState({
    pushEnabled: true,
    emailDigest: true,
    liveAlerts: true,
    messageReplies: true,
    newPrograms: false,
    eventReminders: true,
  });

  const toggleNotifPref = (key: keyof typeof notifPrefs) => {
    setNotifPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Handlers ──

  const handleSaveProfile = () => {
    setProfileData({ ...editForm });
    setEditOpen(false);
    Swal.fire({
      title: "Perfil actualizado",
      text: "Tus datos se guardaron correctamente.",
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#10b981",
      timer: 2500,
      timerProgressBar: true,
      customClass: {
        popup: "rounded-2xl",
        title: "font-bold",
        confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        htmlContainer: "text-sm",
      },
      buttonsStyling: false,
    });
  };

  const handleAvatarSelect = (newAvatar: string) => {
    setAvatarUrl(newAvatar);
    setAvatarPickerOpen(false);
    Swal.fire({
      toast: true,
      position: "top-end",
      title: "Avatar actualizado",
      icon: "success",
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
      buttonsStyling: false,
    });
  };

  const handleLanguageChange = (code: string) => {
    setLanguage(code);
    setLanguageOpen(false);
    const label = LANG_OPTIONS.find((l) => l.code === code)?.label;
    Swal.fire({
      toast: true,
      position: "top-end",
      title: `Idioma cambiado a ${label}`,
      icon: "success",
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
      buttonsStyling: false,
    });
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Cerrar sesión",
      text: "¿Estás seguro de que querés cerrar sesión?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, cerrar sesión",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#d1d5db",
      customClass: {
        popup: "rounded-2xl",
        title: "font-bold",
        confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        cancelButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        htmlContainer: "text-sm",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Sesión cerrada",
          text: "Volvé pronto a RadioFrecuenciaWeb.",
          icon: "success",
          confirmButtonText: "Ir al inicio",
          confirmButtonColor: "#10b981",
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "rounded-2xl",
            title: "font-bold",
            confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
            htmlContainer: "text-sm",
          },
          buttonsStyling: false,
        }).then(() => {
          navigate("/");
        });
      }
    });
  };

  const handleShareProfile = useCallback(async () => {
    const shareUrl = `${window.location.origin}/app/profile`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Perfil de ${profileData.name}`,
          text: `Mirá el perfil de ${profileData.name} en RadioFrecuenciaWeb`,
          url: shareUrl,
        });
      } catch {
        /* cancel */
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      Swal.fire({
        toast: true,
        position: "top-end",
        title: "Enlace copiado",
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
        buttonsStyling: false,
      });
    }
  }, [profileData.name]);

  const handleRemoveFav = (id: number, name: string) => {
    Swal.fire({
      title: "Quitar de favoritas",
      text: `¿Querés eliminar "${name}" de tus favoritas?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, quitar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#d1d5db",
      customClass: {
        popup: "rounded-2xl",
        title: "font-bold",
        confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        cancelButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        htmlContainer: "text-sm",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setFavRadios((prev) => prev.filter((r) => r.id !== id));
        Swal.fire({
          toast: true,
          position: "top-end",
          title: "Eliminada de favoritas",
          icon: "success",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
          buttonsStyling: false,
        });
      }
    });
  };

  const handleUnfollow = (id: number, name: string) => {
    Swal.fire({
      title: "Dejar de seguir",
      text: `¿Querés dejar de seguir a "${name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, dejar de seguir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#d1d5db",
      customClass: {
        popup: "rounded-2xl",
        title: "font-bold",
        confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        cancelButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        htmlContainer: "text-sm",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setFollows((prev) => prev.filter((r) => r.id !== id));
        Swal.fire({
          toast: true,
          position: "top-end",
          title: "Dejaste de seguir esta radio",
          icon: "success",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
          buttonsStyling: false,
        });
      }
    });
  };

  const handleMarkNotifRead = (id: number) => {
    setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
  };

  const handleDeleteNotif = (id: number) => {
    setNotifs((prev) => prev.filter((n) => n.id !== id));
    Swal.fire({
      toast: true,
      position: "top-end",
      title: "Notificación eliminada",
      icon: "success",
      showConfirmButton: false,
      timer: 1500,
      timerProgressBar: true,
      customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
      buttonsStyling: false,
    });
  };

  const handleMarkAllRead = () => {
    setNotifs((prev) => prev.map((n) => ({ ...n, isRead: true })));
    Swal.fire({
      toast: true,
      position: "top-end",
      title: "Todas marcadas como leídas",
      icon: "success",
      showConfirmButton: false,
      timer: 1500,
      timerProgressBar: true,
      customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
      buttonsStyling: false,
    });
  };

  const unreadCount = notifs.filter((n) => !n.isRead).length;

  const notifTypeColors: Record<string, string> = {
    live: "bg-accent-100 text-accent-700",
    message_reply: "bg-primary-100 text-primary-700",
    event_reminder: "bg-amber-100 text-amber-700",
    new_follower: "bg-sky-100 text-sky-700",
    badge: "bg-rose-100 text-rose-700",
    system: "bg-foreground-100 text-foreground-600",
  };

  const notifTypeIcons: Record<string, string> = {
    live: "ri-broadcast-line",
    message_reply: "ri-message-2-line",
    event_reminder: "ri-calendar-event-line",
    new_follower: "ri-music-line",
    badge: "ri-award-line",
    system: "ri-information-line",
  };

  // ── Render ──

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200 safe-top">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer shrink-0 transition-all"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary-500/15 flex items-center justify-center">
              <i className="ri-user-fill text-primary-500 text-lg"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-lg">Mi Perfil</h1>
              <p className="text-foreground-400 text-[11px]">{profileData.username}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 pb-20">
        {/* ── Profile Card ── */}
        <div className="mt-6 bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            {/* Avatar */}
            <div className="relative shrink-0 group">
              <img
                src={avatarUrl}
                alt={profileData.name}
                className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover object-top cursor-pointer"
                onClick={() => setAvatarPickerOpen(true)}
              />
              <div
                className="absolute inset-0 rounded-2xl bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-all flex items-center justify-center cursor-pointer"
                onClick={() => setAvatarPickerOpen(true)}
              >
                <div className="w-8 h-8 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                  <i className="ri-camera-line text-foreground-700 text-sm"></i>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">{profileData.name}</h2>
                <span className="text-sm text-foreground-500 font-medium">{profileData.username}</span>
              </div>
              <p className="text-sm text-foreground-600 mb-3">{profileData.bio}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line"></i>
                  {profileData.location}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-mail-line"></i>
                  {profileData.email}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  Miembro desde{" "}
                  {new Date(listenerProfile.joinDate).toLocaleDateString("es-MX", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Stats badges */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-5">
            {[
              { label: "Horas de escucha", value: listenerProfile.stats.totalListeningHours.toLocaleString(), icon: "ri-headphone-line", color: "bg-primary-100 text-primary-700" },
              { label: "Mensajes enviados", value: listenerProfile.stats.messagesSent.toString(), icon: "ri-message-2-line", color: "bg-accent-100 text-accent-700" },
              { label: "Favoritas", value: favRadios.length.toString(), icon: "ri-heart-line", color: "bg-rose-100 text-rose-700" },
              { label: "Siguiendo", value: follows.length.toString(), icon: "ri-user-follow-line", color: "bg-sky-100 text-sky-700" },
              { label: "Insignias", value: listenerProfile.stats.badges.length.toString(), icon: "ri-award-line", color: "bg-amber-100 text-amber-700" },
            ].map((stat) => (
              <div key={stat.label} className="bg-background-100 rounded-xl p-3 text-center">
                <div className={`w-9 h-9 mx-auto mb-1.5 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <i className={`${stat.icon} text-base`}></i>
                </div>
                <div className="text-lg font-bold text-foreground-950">{stat.value}</div>
                <div className="text-[10px] text-foreground-500 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Insignias */}
          <div className="flex flex-wrap items-center gap-2 mt-5">
            {listenerProfile.stats.badges.map((badge) => (
              <span
                key={badge}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${badgeColors[badge] || "bg-foreground-100 text-foreground-700"}`}
              >
                <i className="ri-verified-badge-line text-xs"></i>
                {badge}
              </span>
            ))}
          </div>
        </div>

        {/* ── Quick Actions ── */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={() => { setEditForm({ ...profileData }); setEditOpen(true); }}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-background-50 border border-background-200 hover:border-foreground-300 rounded-xl text-foreground-700 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line"></i>
            Editar perfil
          </button>
          <button
            onClick={() => setAvatarPickerOpen(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-background-50 border border-background-200 hover:border-foreground-300 rounded-xl text-foreground-700 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-camera-line"></i>
            Cambiar avatar
          </button>
          <button
            onClick={() => setLanguageOpen(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-background-50 border border-background-200 hover:border-foreground-300 rounded-xl text-foreground-700 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-global-line"></i>
            {LANG_OPTIONS.find((l) => l.code === language)?.label || "Idioma"}
          </button>
          <button
            onClick={() => setSection("config")}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-background-50 border border-background-200 hover:border-foreground-300 rounded-xl text-foreground-700 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-settings-3-line"></i>
            Configuración
          </button>
        </div>

        {/* ── Section Tabs ── */}
        <div className="mt-6 sticky top-[57px] z-20 bg-background-50/95 backdrop-blur-sm pt-1 pb-2">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
            {SECTION_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSection(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer relative ${
                  section === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200"
                }`}
              >
                <i className={`${tab.icon} text-xs`}></i>
                {tab.label}
                {tab.key === "notifications" && unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-background-50 text-[9px] font-bold">
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Section Content ── */}
        <div className="mt-2">
          {/* FAVORITES */}
          {section === "favorites" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground-950">
                  Radios Favoritas ({favRadios.length})
                </h3>
                <button
                  onClick={handleShareProfile}
                  className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-500 flex items-center justify-center cursor-pointer transition-all"
                  title="Compartir perfil"
                >
                  <i className="ri-share-forward-line text-sm"></i>
                </button>
              </div>
              {favRadios.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mb-3">
                    <i className="ri-heart-line text-rose-400 text-xl"></i>
                  </div>
                  <p className="text-foreground-600 text-sm font-medium">No tenés radios favoritas</p>
                  <p className="text-foreground-400 text-xs mt-1">Explorá y marcá tus radios preferidas</p>
                  <button
                    onClick={() => navigate("/app/trending")}
                    className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all"
                  >
                    Ver tendencias
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {favRadios.map((radio: FavoriteRadioEntry) => (
                    <div
                      key={radio.id}
                      className="bg-background-50 border border-background-200 rounded-xl p-4 flex items-center gap-3 group hover:border-foreground-300 transition-all cursor-pointer"
                      onClick={() => navigate(`/radio/${stationSlug(radio.stationName)}`)}
                    >
                      <div className="relative shrink-0">
                        <img
                          src={radio.logoUrl}
                          alt={radio.stationName}
                          className="w-12 h-12 rounded-xl object-cover object-top"
                        />
                        {radio.isLive && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground-950 truncate">{radio.stationName}</h4>
                        <div className="flex items-center gap-2 text-xs text-foreground-500 mt-0.5">
                          <span>{radio.frequency} WEB</span>
                          <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                          <span>{radio.category}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFav(radio.id, radio.stationName);
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-rose-400 hover:bg-rose-50 hover:text-rose-600 transition-all cursor-pointer opacity-0 group-hover:opacity-100 shrink-0"
                        title="Quitar de favoritas"
                      >
                        <i className="ri-heart-fill"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* HISTORY */}
          {section === "history" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground-950">Historial de Escucha</h3>
                <span className="text-xs text-foreground-500">
                  {Math.round(listeningHistory.reduce((acc, e) => acc + e.durationMinutes, 0) / 60)}h esta semana
                </span>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden divide-y divide-background-200">
                {listeningHistory.map((entry: ListeningHistoryEntry) => (
                  <div
                    key={entry.id}
                    className="flex items-center gap-3 px-5 py-3.5 hover:bg-background-100/50 transition-all cursor-pointer"
                    onClick={() => navigate(`/radio/${stationSlug(entry.stationName)}`)}
                  >
                    <img
                      src={entry.logoUrl}
                      alt={entry.stationName}
                      className="w-10 h-10 rounded-xl object-cover object-top shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-foreground-950 truncate">{entry.stationName}</h4>
                        <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                          {entry.frequency}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500 truncate mt-0.5">{entry.programName}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-medium text-foreground-700">{formatMinutes(entry.durationMinutes)}</div>
                      <div className="text-[10px] text-foreground-400">{formatRelativeTime(entry.listenedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FOLLOWING */}
          {section === "following" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground-950">
                  Radios Seguidas ({follows.length})
                </h3>
              </div>
              {follows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center mb-3">
                    <i className="ri-user-follow-line text-sky-400 text-xl"></i>
                  </div>
                  <p className="text-foreground-600 text-sm font-medium">No seguís ninguna radio</p>
                  <p className="text-foreground-400 text-xs mt-1">Seguí una radio para recibir sus novedades</p>
                  <button
                    onClick={() => navigate("/app/trending")}
                    className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all"
                  >
                    Explorar radios
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {follows.map((radio: FollowedRadioEntry) => (
                    <div
                      key={radio.id}
                      className="bg-background-50 border border-background-200 rounded-xl p-4 flex items-center gap-3 group hover:border-foreground-300 transition-all cursor-pointer"
                      onClick={() => navigate(`/radio/${stationSlug(radio.stationName)}`)}
                    >
                      <div className="relative shrink-0">
                        <img
                          src={radio.logoUrl}
                          alt={radio.stationName}
                          className="w-12 h-12 rounded-xl object-cover object-top"
                        />
                        {radio.isLive && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground-950 truncate">{radio.stationName}</h4>
                        <div className="flex items-center gap-2 text-xs text-foreground-500 mt-0.5">
                          <span>{radio.frequency} WEB</span>
                          <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                          <span>{radio.listeners.toLocaleString()} oyentes</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {radio.isLive && (
                          <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-[10px] font-semibold whitespace-nowrap">
                            EN VIVO
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnfollow(radio.id, radio.stationName);
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all cursor-pointer opacity-0 group-hover:opacity-100"
                          title="Dejar de seguir"
                        >
                          <i className="ri-user-unfollow-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CONFIGURACIÓN */}
          {section === "config" && (
            <div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">Configuración</h3>

              {/* Cuenta */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Cuenta</h4>
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden divide-y divide-background-200">
                  <button
                    onClick={() => { setEditForm({ ...profileData }); setEditOpen(true); }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-background-100/60 transition-all cursor-pointer text-left"
                  >
                    <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-edit-line text-primary-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-950">Editar perfil</p>
                      <p className="text-xs text-foreground-400">Nombre, bio, ubicación y más</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </button>

                  <button
                    onClick={() => setAvatarPickerOpen(true)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-background-100/60 transition-all cursor-pointer text-left"
                  >
                    <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-camera-line text-accent-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-950">Cambiar avatar</p>
                      <p className="text-xs text-foreground-400">Elegí una imagen de perfil</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </button>

                  <button
                    onClick={() => setLanguageOpen(true)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-background-100/60 transition-all cursor-pointer text-left"
                  >
                    <div className="w-9 h-9 rounded-xl bg-secondary-100 flex items-center justify-center shrink-0">
                      <i className="ri-global-line text-secondary-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-950">Idioma</p>
                      <p className="text-xs text-foreground-400">{LANG_OPTIONS.find((l) => l.code === language)?.label || "Español"}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </button>
                </div>
              </div>

              {/* Preferencias de notificaciones */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Preferencias de notificaciones</h4>
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden divide-y divide-background-200">
                  {[
                    { key: "pushEnabled" as const, label: "Notificaciones push", desc: "Recibí alertas en tu navegador" },
                    { key: "emailDigest" as const, label: "Resumen por email", desc: "Un resumen semanal de novedades" },
                    { key: "liveAlerts" as const, label: "Radios en vivo", desc: "Cuando tus radios favoritas transmiten" },
                    { key: "messageReplies" as const, label: "Respuestas a mensajes", desc: "Cuando te respondan al aire" },
                    { key: "newPrograms" as const, label: "Programas nuevos", desc: "Nuevos programas en tus categorías" },
                    { key: "eventReminders" as const, label: "Recordatorios de eventos", desc: "Eventos de tus radios seguidas" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between px-5 py-3.5">
                      <div className="flex-1 min-w-0 mr-4">
                        <p className="text-sm font-medium text-foreground-950">{item.label}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => toggleNotifPref(item.key)}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          notifPrefs[item.key] ? "bg-primary-500" : "bg-foreground-200"
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all shadow-sm ${
                            notifPrefs[item.key] ? "left-[22px]" : "left-0.5"
                          }`}
                        ></div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Privacidad */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Privacidad</h4>
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden divide-y divide-background-200">
                  <button
                    onClick={() => {
                      Swal.fire({
                        title: "Privacidad",
                        text: "Esta sección estará disponible pronto con la integración de cuentas. Podrás gestionar quién ve tu perfil, tu historial y tus mensajes.",
                        icon: "info",
                        confirmButtonText: "Entendido",
                        confirmButtonColor: "#10b981",
                        customClass: {
                          popup: "rounded-2xl",
                          title: "font-bold",
                          confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
                          htmlContainer: "text-sm",
                        },
                        buttonsStyling: false,
                      });
                    }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-background-100/60 transition-all cursor-pointer text-left"
                  >
                    <div className="w-9 h-9 rounded-xl bg-foreground-100 flex items-center justify-center shrink-0">
                      <i className="ri-shield-check-line text-foreground-500"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-950">Privacidad de cuenta</p>
                      <p className="text-xs text-foreground-400">Gestioná quién ve tu actividad</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </button>
                </div>
              </div>

              {/* Acerca de */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Acerca de</h4>
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden divide-y divide-background-200">
                  <div className="flex items-center justify-between px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-foreground-100 flex items-center justify-center shrink-0">
                        <i className="ri-information-line text-foreground-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">RadioFrecuenciaWeb</p>
                        <p className="text-xs text-foreground-400">Versión 1.0.0</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cerrar sesión */}
              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">Sesión</h4>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-5 py-3.5 bg-rose-50 border border-rose-200 hover:bg-rose-100 rounded-2xl transition-all cursor-pointer text-left"
                >
                  <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                    <i className="ri-logout-box-line text-rose-500"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-rose-600">Cerrar sesión</p>
                    <p className="text-xs text-rose-400">Volvé cuando quieras</p>
                  </div>
                  <i className="ri-logout-box-r-line text-rose-400"></i>
                </button>
              </div>
            </div>
          )}

          {/* NOTIFICATIONS */}
          {section === "notifications" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-foreground-950">Notificaciones</h3>
                  {unreadCount > 0 && (
                    <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-[10px] font-bold rounded-full">
                      {unreadCount} nuevas
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap transition-all"
                  >
                    Marcar todas leídas
                  </button>
                )}
              </div>
              {notifs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-14 h-14 rounded-full bg-foreground-100 flex items-center justify-center mb-3">
                    <i className="ri-notification-3-line text-foreground-400 text-xl"></i>
                  </div>
                  <p className="text-foreground-600 text-sm font-medium">No tenés notificaciones</p>
                  <p className="text-foreground-400 text-xs mt-1">Aquí aparecerán tus avisos y novedades</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {notifs.map((notif: NotificationEntry) => (
                    <div
                      key={notif.id}
                      className={`relative bg-background-50 border rounded-xl px-4 py-3.5 flex items-start gap-3 cursor-pointer hover:bg-background-100/80 transition-all group ${
                        !notif.isRead ? "border-primary-200/60 ring-1 ring-primary-200/40" : "border-background-200"
                      }`}
                      onClick={() => {
                        handleMarkNotifRead(notif.id);
                        if (notif.actionUrl && notif.actionUrl !== "/listener") {
                          navigate(notif.actionUrl);
                        }
                      }}
                    >
                      {!notif.isRead && (
                        <div className="absolute top-3.5 right-4 w-2 h-2 bg-primary-500 rounded-full"></div>
                      )}
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          notifTypeColors[notif.type] || "bg-foreground-100 text-foreground-600"
                        }`}
                      >
                        <i className={notifTypeIcons[notif.type] || "ri-notification-line"}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4
                          className={`text-sm ${
                            !notif.isRead ? "font-bold text-foreground-950" : "font-semibold text-foreground-800"
                          }`}
                        >
                          {notif.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] text-foreground-400">{formatRelativeTime(notif.timestamp)}</span>
                          {notif.stationName && (
                            <>
                              <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                              <span className="text-[10px] text-foreground-500">{notif.stationName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotif(notif.id);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200 cursor-pointer transition-all opacity-0 group-hover:opacity-100 shrink-0"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* ── Edit Profile Modal ── */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setEditOpen(false)}
          ></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground-950">Editar Perfil</h3>
              <button
                onClick={() => setEditOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Usuario</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  rows={3}
                  maxLength={200}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                />
                <div className="text-xs text-foreground-400 mt-1">{editForm.bio.length}/200</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Ubicación</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditOpen(false)}
                className="flex-1 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveProfile}
                className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Avatar Picker Modal ── */}
      {avatarPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setAvatarPickerOpen(false)}
          ></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground-950">Cambiar Avatar</h3>
              <button
                onClick={() => setAvatarPickerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-4">Seleccioná una imagen para tu perfil.</p>
            <div className="grid grid-cols-3 gap-3">
              {MOCK_AVATARS.map((url, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAvatarSelect(url)}
                  className={`relative rounded-xl overflow-hidden cursor-pointer transition-all border-2 ${
                    avatarUrl === url
                      ? "border-primary-500 ring-2 ring-primary-200"
                      : "border-transparent hover:border-foreground-200"
                  }`}
                >
                  <img src={url} alt={`Avatar opción ${idx + 1}`} className="w-full aspect-square object-cover object-top" />
                  {avatarUrl === url && (
                    <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-background-50 text-xs"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setAvatarPickerOpen(false)}
              className="w-full mt-4 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Language Modal ── */}
      {languageOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setLanguageOpen(false)}
          ></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground-950">Idioma</h3>
              <button
                onClick={() => setLanguageOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-4">Seleccioná el idioma de la aplicación.</p>
            <div className="space-y-2">
              {LANG_OPTIONS.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    language === lang.code
                      ? "bg-primary-500/10 border-2 border-primary-500 text-primary-700"
                      : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.label}</span>
                  {language === lang.code && (
                    <div className="ml-auto w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <i className="ri-check-line text-background-50 text-xs"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setLanguageOpen(false)}
              className="w-full mt-4 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}